import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import {
  Bar,
  BarChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ArrowDownLeft, ArrowUpRight, RotateCcw, ShoppingBag } from 'lucide-react';
import { formatMoney } from '@/utils/currency';

const PIE_COLORS = ['#4f46e5', '#0ea5e9', '#10b981', '#f59e0b', '#f43f5e', '#94a3b8'];

type TrendPoint = { month: string; label: string; total: number };
type CategoryPoint = { category: string; amount: number; percent: number };
type Insight = {
  avg_order_value?: number;
  purchase_frequency?: number;
  last_purchase?: string | null;
  lifetime_value?: number;
  order_count?: number;
};
type Txn = {
  posting_date?: string | null;
  voucher_no?: string;
  txn_type?: string;
  amount?: number;
  description?: string;
};

interface CustomerOverviewPanelProps {
  purchaseTrend?: TrendPoint[];
  topCategories?: CategoryPoint[];
  insights?: Insight | null;
  recentTransactions?: Txn[];
  onOpenVoucher?: (voucherNo: string, txnType: string) => void;
}

function TxnIcon({ type }: { type?: string }) {
  if (type === 'Payment') return <ArrowDownLeft className="w-3.5 h-3.5 text-emerald-600" />;
  if (type === 'Return') return <RotateCcw className="w-3.5 h-3.5 text-amber-600" />;
  return <ShoppingBag className="w-3.5 h-3.5 text-indigo-600" />;
}

export default function CustomerOverviewPanel({
  purchaseTrend = [],
  topCategories = [],
  insights,
  recentTransactions = [],
  onOpenVoucher,
}: CustomerOverviewPanelProps) {
  const { t } = useTranslation();

  const insightRows = [
    {
      label: t('customers.dashboard.avgOrderValue', 'Avg. Order Value'),
      value: formatMoney(insights?.avg_order_value || 0),
    },
    {
      label: t('customers.dashboard.purchaseFrequency', 'Purchase Frequency'),
      value: `${insights?.purchase_frequency ?? 0}/mo`,
    },
    {
      label: t('customers.dashboard.lastPurchase', 'Last Purchase'),
      value: insights?.last_purchase
        ? format(new Date(insights.last_purchase), 'dd MMM yyyy')
        : '—',
    },
    {
      label: t('customers.dashboard.lifetimeValue', 'LTV'),
      value: formatMoney(insights?.lifetime_value || 0),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-1 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-800 mb-3">
            {t('customers.dashboard.purchaseTrend', 'Purchase Trend')}
          </h3>
          <div className="h-44">
            {purchaseTrend.length === 0 ? (
              <p className="text-sm text-slate-400 pt-10 text-center">No sales yet</p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={purchaseTrend} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <Tooltip
                    formatter={(v: number) => formatMoney(v)}
                    contentStyle={{ borderRadius: 12, borderColor: '#e2e8f0', fontSize: 12 }}
                  />
                  <Bar dataKey="total" fill="#6366f1" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-800 mb-3">
            {t('customers.dashboard.insights', 'Customer Insights')}
          </h3>
          <dl className="space-y-3">
            {insightRows.map((row) => (
              <div key={row.label} className="flex items-center justify-between gap-3">
                <dt className="text-xs text-slate-500">{row.label}</dt>
                <dd className="text-sm font-semibold text-slate-900 tabular-nums">{row.value}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-800 mb-3">
            {t('customers.dashboard.topCategories', 'Top Categories')}
          </h3>
          {topCategories.length === 0 ? (
            <p className="text-sm text-slate-400 pt-8 text-center">No category data</p>
          ) : (
            <div className="flex items-center gap-3">
              <div className="w-28 h-28 shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={topCategories}
                      dataKey="amount"
                      nameKey="category"
                      innerRadius={28}
                      outerRadius={48}
                      paddingAngle={2}
                    >
                      {topCategories.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <ul className="space-y-1.5 min-w-0 flex-1">
                {topCategories.map((c, i) => (
                  <li key={c.category} className="flex items-center gap-2 text-xs">
                    <span
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }}
                    />
                    <span className="truncate text-slate-700 flex-1">{c.category}</span>
                    <span className="tabular-nums text-slate-500">{c.percent}%</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-800">
            {t('customers.dashboard.recentTransactions', 'Recent Transactions')}
          </h3>
        </div>
        {recentTransactions.length === 0 ? (
          <p className="p-8 text-center text-sm text-slate-400">No transactions yet</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-2.5">Date</th>
                  <th className="px-4 py-2.5">Ref</th>
                  <th className="px-4 py-2.5">Type</th>
                  <th className="px-4 py-2.5">Description</th>
                  <th className="px-4 py-2.5 text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {recentTransactions.map((txn, idx) => {
                  const isOut = txn.txn_type === 'Sale';
                  const isIn = txn.txn_type === 'Payment' || txn.txn_type === 'Return';
                  return (
                    <tr key={`${txn.voucher_no}-${idx}`} className="hover:bg-slate-50/80">
                      <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">
                        {txn.posting_date
                          ? format(new Date(txn.posting_date), 'dd MMM yyyy')
                          : '—'}
                      </td>
                      <td className="px-4 py-2.5">
                        {txn.voucher_no ? (
                          <button
                            type="button"
                            className="font-medium text-primary-700 hover:underline"
                            onClick={() =>
                              onOpenVoucher?.(txn.voucher_no!, txn.txn_type || 'Sale')
                            }
                          >
                            {txn.voucher_no}
                          </button>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700">
                          <TxnIcon type={txn.txn_type} />
                          {txn.txn_type}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-slate-500">{txn.description || txn.txn_type}</td>
                      <td
                        className={`px-4 py-2.5 text-right tabular-nums font-semibold ${
                          isIn ? 'text-emerald-700' : isOut ? 'text-slate-900' : 'text-slate-700'
                        }`}
                      >
                        {isIn ? (
                          <span className="inline-flex items-center gap-0.5 justify-end">
                            <ArrowDownLeft className="w-3 h-3" />
                            {formatMoney(txn.amount || 0)}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-0.5 justify-end">
                            <ArrowUpRight className="w-3 h-3 text-slate-400" />
                            {formatMoney(txn.amount || 0)}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
